/**
 * Parse a timestamp as written by SQLite's `datetime('now')`.
 *
 * Those values are UTC in the form `2026-09-09 11:00:00` — no `T`, no zone.
 * Two things went wrong with that around the app:
 *
 *  1. `new Date('2026-09-09 11:00:00')` is treated as LOCAL time by V8, so a
 *     UTC value rendered hours off (10 in UTC+10).
 *  2. The `value + 'Z'` workaround produces `2026-09-09 11:00:00Z`, which V8
 *     tolerates but Firefox and Safari reject as Invalid Date.
 *
 * Normalising to real ISO-8601 fixes both.
 */
/** Does the string already state its timezone? A bare `T` does not count. */
const HAS_ZONE = /(?:Z|[+-]\d{2}:?\d{2})$/;

export function parseServerDate(value: string | null | undefined): Date | null {
  if (!value) return null;

  // Two shapes come out of this database, both UTC and neither zone-stamped:
  //   checks.timestamp   -> '2026-09-09 11:00:00'  (datetime('now'))
  //   checks_hourly.hour -> '2026-09-09T11:00:00'  (strftime with a literal T)
  // JS reads the first as local and the second as local too, so both need Z.
  const normalised = HAS_ZONE.test(value)
    ? value
    : `${value.replace(' ', 'T')}Z`;

  const date = new Date(normalised);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDateTime(value: string | null | undefined): string {
  return parseServerDate(value)?.toLocaleString() ?? '—';
}

export function formatTime(value: string | null | undefined): string {
  return parseServerDate(value)?.toLocaleTimeString() ?? '—';
}

export function formatDate(value: string | null | undefined): string {
  return parseServerDate(value)?.toLocaleDateString() ?? '—';
}
